import React, { FC } from "react";
import { Box, MenuItem, Select, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { HAMASPIK_LABEL_SX } from "../../../../../assets/hamaspik/theme";
import { PostIfLookupItemDto, PostIfPositionRulesDto } from "../../../../../types/pifDto";
import { getPifRateVerdict, isBound } from "../../pifValidation";
import { PifFormValues, toDecimalInput, toSelectId } from "../../types";
import PifSectionCard from "./PifSectionCard";

interface PifSectionPayProps {
  values: PifFormValues;
  onChange: (patch: Partial<PifFormValues>) => void;
  rules: PostIfPositionRulesDto | null;
  rateTypes: PostIfLookupItemDto[];
}

const PifSectionPay: FC<PifSectionPayProps> = ({ values: f, onChange, rules, rateTypes }) => {
  const verdict = getPifRateVerdict(f.rate, rules);

  // The bar is only meaningful with a real position band; a null bound means
  // HR_PositionRates has no row, which must not be drawn as a zero-width band.
  const positionMin = isBound(rules?.positionMin) ? rules.positionMin : 0;
  const positionMax = isBound(rules?.positionMax) ? rules.positionMax : 0;
  const hasBand = isBound(rules?.positionMin) && isBound(rules?.positionMax) && positionMax > positionMin;
  const span = hasBand ? positionMax - positionMin : 1;
  const clamp = (v: number) => Math.max(0, Math.min(100, v));
  const hiringMin = isBound(rules?.hiringMin) ? rules.hiringMin : 0;
  const hiringMax = isBound(rules?.hiringMax) ? rules.hiringMax : 0;
  const showHiring = hasBand && isBound(rules?.hiringMin) && isBound(rules?.hiringMax);
  const hLeft = showHiring ? clamp(((hiringMin - positionMin) / span) * 100) : 0;
  const hWidth = showHiring ? clamp(((hiringMax - hiringMin) / span) * 100) : 0;
  const rateNum = parseFloat(f.rate);
  const rLeft = hasBand && verdict.hasRate ? clamp(((rateNum - positionMin) / span) * 100) : 0;

  const bandMsgColor = !rules || !verdict.hasRate
    ? "text.secondary"
    : verdict.blocked
      ? "error.main"
      : verdict.warn
        ? "warning.dark"
        : "success.dark";

  return (
    <PifSectionCard id="sec-pay" title="Pay">
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 2, alignItems: "start" }}>
        <Field label="Rate type *">
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={f.rateTypeId ?? ""}
            onChange={(e) => {
              const id = toSelectId(e.target.value);
              onChange({ rateTypeId: id, rateType: rateTypes.find((r) => r.id === id)?.text ?? "" });
            }}
          >
            <MenuItem value="">Choose a rate type…</MenuItem>
            {rateTypes.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.text}
              </MenuItem>
            ))}
          </Select>
        </Field>
        <Field label="Rate (USD) *">
          <TextField
            size="small"
            fullWidth
            value={f.rate}
            inputMode="decimal"
            placeholder="0.00"
            error={verdict.blocked}
            onChange={(e) => onChange({ rate: toDecimalInput(e.target.value) })}
            data-testid="postif-rate"
          />
          {verdict.blocked && (
            <Typography variant="caption" color="error.main" data-testid="postif-rate-error">
              {verdict.message}
            </Typography>
          )}
        </Field>
        <Box
          sx={{
            p: 1.875,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.25, alignItems: "baseline" }}>
            <Typography variant="caption" fontWeight={700} letterSpacing="0.08em" color="text.secondary">
              PAY BANDS
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {hasBand ? `Position $${positionMin}–$${positionMax}` : rules ? "No band on file" : "Pick a position"}
            </Typography>
          </Box>
          <Box sx={{ py: 0.5 }}>
            <Box sx={{ position: "relative", height: 12, bgcolor: "grey.200", borderRadius: 999, overflow: "visible" }}>
              {showHiring && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${hLeft}%`,
                    width: `${hWidth}%`,
                    bgcolor: "#F8A87E",
                    borderRadius: 999,
                  }}
                />
              )}
              {hasBand && verdict.hasRate && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -4,
                    bottom: -4,
                    left: `calc(${rLeft}% - 2px)`,
                    width: 4,
                    borderRadius: 999,
                    bgcolor: verdict.blocked ? "error.main" : verdict.warn ? "warning.main" : "success.main",
                  }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary">
              {hasBand ? `$${positionMin}` : "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {hasBand ? `$${positionMax}` : "—"}
            </Typography>
          </Box>
          <Typography variant="caption" color={bandMsgColor} sx={{ lineHeight: 1.45 }}>
            {verdict.message}
          </Typography>
        </Box>
      </Box>

      {rules?.mayOperateVehicle && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3.25, alignItems: "flex-start", pt: 1.75, borderTop: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={HAMASPIK_LABEL_SX}>Will operate an agency vehicle?</Typography>
            <Box sx={{ display: "flex", gap: 1.25 }}>
              <Button variant={f.vehicle === "yes" ? "contained" : "outlined"} size="small" onClick={() => onChange({ vehicle: "yes" })}>
                Yes
              </Button>
              <Button
                variant={f.vehicle === "no" ? "contained" : "outlined"}
                color={f.vehicle === "no" ? "secondary" : "primary"}
                size="small"
                onClick={() => onChange({ vehicle: "no" })}
              >
                No
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1.75, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography sx={HAMASPIK_LABEL_SX}>Notes for the approvers</Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          value={f.notes}
          placeholder="Anything unusual about the rate, the schedule, or the start date."
          onChange={(e) => onChange({ notes: e.target.value })}
        />
      </Box>
    </PifSectionCard>
  );
};

const Field: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography sx={HAMASPIK_LABEL_SX}>{label}</Typography>
    {children}
  </Box>
);

export default PifSectionPay;
